import {
  Box,
  Button,
  FormControl,
  FormLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import Editor from "@ckeditor/ckeditor5-build-classic";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { ChangeEvent, useEffect, useRef, useState } from "react";
import { boxStyle, configToast, statusTypes } from "../../../constants";
import {
  postCourseService,
  postCourseUploadService,
} from "../../../services/course.service";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../state/hook";
import { setApp } from "../../../state/libs/appSlice";

const contentToolbar = {
  toolbar: {
    title: "การจัดการหลักสูตร",
    subtitles: ["รายการหลักสูตร", "สร้างหลักสูตร"],
  },
};

const CreateCourse = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [courseFile, setCourseFile] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const inputRef = useRef<HTMLInputElement | any>(null);

  const handleUploadClick = () => {
    if (inputRef.current) inputRef.current.click();
  };
  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) {
      return;
    }
    const file = e.target.files[0];
    setCourseFile(file);
    inputRef.current.value = "";
  };

  const handleNavigateCourseList = () => {
    navigate("/course-list");
  };

  const handleLoading = () => setLoading(true);
  const handleLoaded = () => setLoading(false);

  const { register, handleSubmit, formState, setValue } = useForm({
    defaultValues: {
      title: "",
      question: "",
      description: "",
      is_publish: null,
    },
  });
  const { isValid, isDirty } = formState;
  const onError = (errors: any, e: any) => console.log(errors, e);

  const editorDescriptionConfig = { placeholder: "รายละเอียด" };
  const editorQuestionConfig = { placeholder: "คำถาม" };

  const handleEditorDescriptionChange = (event: any, editor: any) => {
    const data = editor.getData();
    setValue("description", data);
  };

  const handleEditorQuestionChange = (event: any, editor: any) => {
    const data = editor.getData();
    setValue("question", data);
  };

  const onSubmitHandler: SubmitHandler<any> = async (values) => {
    try {
      handleLoading();
      const courseCreated = await postCourseService({
        ...values,
        is_publish: !values.is_publish ? true : false,
      });

      const formdata = new FormData();
      formdata.append("file", courseFile as File);
      postCourseUploadService(courseCreated.data, formdata);

      toast.success("สร้างหลักสูตรสำเร็จ", {
        ...configToast,
        onClose: () => handleNavigateCourseList(),
      });
    } catch (error: any) {
      const resMessage =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      toast.error(resMessage || "สร้างหลักสูตรไม่สำเร็จ", configToast);
      handleLoaded();
    }
  };

  const setContentToolbar = () => {
    dispatch(setApp(contentToolbar));
  };

  useEffect(setContentToolbar, [dispatch]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmitHandler, onError)}
      display="flex"
      flexDirection={{ xs: "column-reverse", md: "row" }}
      gap="16px"
    >
      <Box
        sx={{ ...boxStyle, height: "fit-content" }}
        width={{ xs: 1, md: "60%" }}
      >
        <FormControl>
          <TextField
            fullWidth
            type="text"
            label="ชื่อคอร์สเรียน"
            placeholder="กรอกชื่อคอร์สเรียน"
            {...register("title", { required: true })}
            size="medium"
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            <Typography typography="subtitle2" color="black.main">
              รายละเอียด
            </Typography>
          </FormLabel>
          <CKEditor
            editor={Editor}
            config={editorDescriptionConfig}
            onChange={handleEditorDescriptionChange}
          />
        </FormControl>

        <FormControl>
          <FormLabel>
            <Typography typography="subtitle2" color="black.main">
              คำถาม
            </Typography>
          </FormLabel>
          <CKEditor
            editor={Editor}
            onChange={handleEditorQuestionChange}
            config={editorQuestionConfig}
          />
        </FormControl>

        <FormControl>
          <InputLabel id="status-label">สถานะ</InputLabel>
          <Select
            labelId="status-label"
            id="status-label"
            placeholder="เลือกสถานะ"
            label="สถานะ"
            {...register("is_publish", { required: true })}
            size="medium"
          >
            {statusTypes.map((status: string, index: number) => (
              <MenuItem key={index} value={index}>
                {status}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box display="flex" justifyContent="center" gap="16px">
          <Box>
            <Button
              variant="contained"
              startIcon={<SaveIcon sx={{ color: "white" }} />}
              color="green"
              size="large"
              type="submit"
              disabled={!isValid || !isDirty || !courseFile || loading}
            >
              <Typography color="white">บันทึก</Typography>
            </Button>
          </Box>

          <Box>
            <Button
              variant="contained"
              startIcon={<CancelIcon sx={{ color: "white" }} />}
              color="red"
              size="large"
              type="button"
              component={RouterLink}
              to="/course-list"
            >
              <Typography color="white">ยกเลิก</Typography>
            </Button>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{ ...boxStyle, height: "fit-content" }}
        width={{ xs: 1, md: "40%" }}
      >
        <Typography color="cGrey.main">อัพโหลดไฟล์</Typography>

        <Box>
          <Button variant="contained" color="green" onClick={handleUploadClick}>
            <Box
              component="input"
              hidden
              accept="application/pdf"
              type="file"
              ref={inputRef}
              onChange={handleFileChange}
            />
            <Typography color="white">UPLOAD FILE</Typography>
          </Button>
        </Box>

        {courseFile && (
          <Typography color="cGrey.main">{courseFile.name}</Typography>
        )}
      </Box>
    </Box>
  );
};

export default CreateCourse;
